import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  useTheme,
  CircularProgress,
  Chip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Alert,
  TablePagination,
  IconButton,
  Collapse,
} from "@mui/material";
import EmailIcon from "@mui/icons-material/Email";
import GetAppIcon from "@mui/icons-material/GetApp";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import DescriptionIcon from "@mui/icons-material/Description";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import TableViewIcon from "@mui/icons-material/TableView";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import axios from "../../utils/axiosInstance";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import ConfirmDialog from "../ConfirmDialog";
import { toast } from "react-toastify";
import { useParams } from "react-router-dom";
import { getUIDescription } from "../SiteAdmin/customerGroupDetails/AssigningCustomerGroupDevice";

export default function EmailSearch() {
  const { emailAddress } = useParams();
  const theme = useTheme();
  const [recipients, setRecipients] = useState([]);
  const [emailQuery, setEmailQuery] = useState("");
  const [filteredRecipients, setFilteredRecipients] = useState([]);
  const [loadingData, setLoadingData] = useState(false);
  const [exportMenuAnchor, setExportMenuAnchor] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [error, setError] = useState("");
  const [selectedRecipient, setSelectedRecipient] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [expandedRows, setExpandedRows] = useState({});
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const toggleRowExpansion = (rowId) => {
    setExpandedRows((prev) => ({
      ...prev,
      [rowId]: !prev[rowId],
    }));
  };

  const sortRecipients = (recipientsToSort) => {
    if (!sortConfig.key) return recipientsToSort;

    return [...recipientsToSort].sort((a, b) => {
      const aValue = a[sortConfig.key] || "";
      const bValue = b[sortConfig.key] || "";

      if (typeof aValue === "string") {
        if (sortConfig.direction === "asc") return aValue.localeCompare(bValue);
        else return bValue.localeCompare(aValue);
      } else if (typeof aValue === "number") {
        if (sortConfig.direction === "asc") return aValue - bValue;
        else return bValue - aValue;
      } else {
        return 0;
      }
    });
  };

  const fetchRecipientsByEmail = async (email) => {
    if (!email) return;

    try {
      setLoadingData(true);
      setError("");

      let Api_return = await axios.get(
        `${process.env.REACT_APP_API_GATEAWAY}/recipients?email=${email}`,
      );

      const data = Api_return.data;
      setRecipients(data);
      setFilteredRecipients(data);
      setPage(0);
    } catch (err) {
      console.error("Error fetching recipients by email:", err);
      setError(`No recipients found for email address: ${email}`);
    } finally {
      setLoadingData(false);
    }
  };

  useEffect(() => {
    if (emailAddress) {
      fetchRecipientsByEmail(emailAddress);
    }
  }, [emailAddress]);

  const handleEmailSearch = (e) => {
    const q = e.target.value.toLowerCase();
    setEmailQuery(e.target.value);
    const filtered = recipients.filter((recipient) => {
      const email = recipient.user?.email || "";
      const name = recipient.user?.name || "";
      const UnitName = recipient.device?.name || "";
      const UnitId = recipient.device?.id || "";
      return (
        email.toLowerCase().includes(q) ||
        name.toLowerCase().includes(q) ||
        UnitName.toLowerCase().includes(q) ||
        UnitId.toLowerCase().includes(q)
      );
    });
    setFilteredRecipients(filtered);
    setPage(0);
  };

  const refreshData = () => {
    if (emailAddress) {
      fetchRecipientsByEmail(emailAddress);
    } else if (emailQuery.trim()) {
      fetchRecipientsByEmail(emailQuery.trim());
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleExportMenuOpen = (event) => {
    setExportMenuAnchor(event.currentTarget);
  };

  const handleExportMenuClose = () => {
    setExportMenuAnchor(null);
  };

  const exportToCSV = () => {
    const csvContent = [
      ["Name", "Email Address", "Unit Name", "Modem Serial Number"],
      ...filteredRecipients.map((recipient) => [
        recipient.user?.name,
        recipient.user?.email,
        recipient.device?.name,
        recipient.device?.id,
      ]),
    ];

    const csvString = csvContent
      .map((row) => row.map((field) => `"${field}"`).join(","))
      .join("\n");

    const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `recipients_${emailAddress || emailQuery}.csv`;
    link.click();

    handleExportMenuClose();
    toast.success("Exported to CSV");
  };

  const exportToExcel = () => {
    const headers = [
      "Name",
      "Email Address",
      "Unit Name",
      "Modem Serial Number",
    ];
    const rows = filteredRecipients.map((recipient) => [
      recipient.user?.name,
      recipient.user?.email,
      recipient.device?.name,
      recipient.device?.id,
    ]);

    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Recipients");
    XLSX.writeFile(workbook, `recipients_${emailAddress || emailQuery}.xlsx`);

    handleExportMenuClose();
    toast.success("Exported to excel");
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.text(`Recipients for Email: ${emailAddress || emailQuery}`, 14, 20);

    const headers = [
      ["Name", "Email Address", "Unit Name", "Modem Serial Number"],
    ];
    const rows = filteredRecipients.map((recipient) => [
      recipient.user?.name,
      recipient.user?.email,
      recipient.device?.name,
      recipient.device?.id,
      recipient.date_created,
    ]);

    autoTable(doc, {
      head: headers,
      body: rows,
      startY: 30,
      theme: "grid",
      styles: { fontSize: 10 },
      headStyles: { fillColor: [41, 128, 185] },
    });

    doc.save(`recipients_${emailAddress || emailQuery}.pdf`);
    handleExportMenuClose();
    toast.success("Exported to PDF");
  };

  const copyToClipboard = () => {
    const headers = "Name\tEmail Address\tUnit Name\tModem Serial Number";
    const textContent = filteredRecipients
      .map(
        (recipient) =>
          `${recipient.user?.name}\t${recipient.user?.email}\t${recipient.device?.name}\t${recipient.device?.id}`,
      )
      .join("\n");

    navigator.clipboard
      .writeText(`${headers}\n${textContent}`)
      .then(() => console.log("Recipients data copied to clipboard"))
      .catch((err) => console.error("Failed to copy:", err));

    handleExportMenuClose();
  };

  const handleDeleteClick = (recipient) => {
    setSelectedRecipient(recipient);
    setConfirmOpen(true);
  };

  const handleDeleteRecipient = async (recipient) => {
    try {
      const deviceId = recipient?.device?.id;
      const userId = recipient?.user?.id;
      const memberId = recipient?.memberId;
      const source = recipient?.source;

      if (source === "membership") {
        if (!memberId) {
          console.error("❌ Missing memberId for membership deletion");
          return;
        }

        await axios.delete(
          `${process.env.REACT_APP_API_GATEAWAY}/members/${memberId}/hard`,
          {
            data: {
              UI_Description: `${getUIDescription()}\n delete recipient`,
            },
          },
        );

        console.log("✅ Membership deleted:", recipient);

        setRecipients((prev) => prev.filter((r) => r?.memberId !== memberId));
        setFilteredRecipients((prev) =>
          prev.filter((r) => r?.memberId !== memberId),
        );
      } else {
        if (!deviceId || !userId) {
          console.error("❌ Missing deviceId or userId for recipient deletion");
          return;
        }

        await axios.delete(
          `${process.env.REACT_APP_API_GATEAWAY}/recipients/hard?deviceId=${deviceId}&userId=${userId}`,
          {
            data: {
              UI_Description: `${getUIDescription()}\n Delete Recipient from Email Search tab`,
            },
          },
        );

        console.log("✅ Recipient deleted:", recipient);

        setRecipients((prev) =>
          prev.filter(
            (r) => !(r?.device?.id === deviceId && r?.user?.id === userId),
          ),
        );
        setFilteredRecipients((prev) =>
          prev.filter(
            (r) => !(r?.device?.id === deviceId && r?.user?.id === userId),
          ),
        );
      }
    } catch (error) {
      console.error(
        "❌ Error deleting recipient or membership:",
        error.response?.data || error.message,
      );
    } finally {
      setConfirmOpen(false);
      setSelectedRecipient(null);
    }
  };

  const sortedRecipients = sortRecipients(filteredRecipients);
  const paginatedRecipients = sortedRecipients.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  return (
    <Box
      sx={{
        flexGrow: 1,
        p: { xs: 0, sm: 0 },
        height: "100%",
        position: "relative",
      }}
    >
      {loadingData && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            bgcolor: "rgba(187, 183, 183, 0.12)",
            backdropFilter: "blur(10px)",
            zIndex: 10,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <CircularProgress color="#60a5fa" size={70} />
        </Box>
      )}

      <Paper
        elevation={4}
        sx={{
          p: 1.5,
          display: "flex",
          flexDirection: "column",
          height: "100%",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
            flexWrap: "wrap",
            gap: 1,
            pr: { xs: 0, sm: 1 },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              height: "100%",
            }}
          >
            <EmailIcon color="primary" />
            <Typography
              fontSize={{ xs: "1rem", sm: "1.5rem", md: "1.8rem" }}
              fontWeight="bold"
            >
              Recipients for: {emailAddress || emailQuery || "Email Address"}
            </Typography>
            <Chip
              label={`${filteredRecipients.length} results`}
              color="primary"
              size="small"
            />
          </Box>

          <Box
            sx={{ display: "flex", gap: 1, flexWrap: "wrap", height: "100%" }}
          >
            <Button
              variant="contained"
              sx={{
                borderRadius: 0,
                bgcolor: "#53e546",
                "&:hover": { bgcolor: "#45c436" },
              }}
              onClick={refreshData}
            >
              Refresh Data
            </Button>
            <Button
              sx={{ borderRadius: 0 }}
              variant="outlined"
              color="white"
              startIcon={<GetAppIcon />}
              onClick={handleExportMenuOpen}
              disabled={filteredRecipients.length === 0}
            >
              Export
            </Button>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            gap: 2,
            mb: 2,
            alignItems: "center",
            height: "100%",
          }}
        >
          <TextField
            label="Search"
            placeholder="Search by Unit name, Modem Serial Number"
            variant="outlined"
            value={emailQuery}
            onChange={handleEmailSearch}
            sx={{
              width: "50%",
              "& .MuiOutlinedInput-root": {
                borderRadius: 0,
              },
            }}
          />
        </Box>

        {error && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <TableContainer
          sx={{
            flexGrow: 1,
            height: { xs: "50vh", md: "calc(100vh - 280px)" },
            overflow: "auto",
            WebkitOverflowScrolling: "touch",
            overscrollBehaviorY: "none",
          }}
        >
          <Table stickyHeader>
            <TableHead>
              <TableRow sx={{ backgroundColor: theme.palette.grey[100] }}>
                {/* Mobile expand button column */}
                <TableCell
                  sx={{
                    display: { xs: "table-cell", md: "none" },
                    fontWeight: "bold",
                    textAlign: "center",
                    fontSize: "12px",
                    padding: "12px 8px",
                    width: "50px",
                  }}
                >
                  Details
                </TableCell>
                <TableCell
                  sx={{
                    textAlign: "center",
                    fontWeight: "bold",
                    fontSize: { xs: "11px", sm: "14px", md: "15px" },
                    padding: "8px",
                  }}
                >
                  Name
                </TableCell>
                <TableCell
                  sx={{
                    textAlign: "center",
                    fontWeight: "bold",
                    fontSize: { xs: "11px", sm: "14px", md: "15px" },
                    padding: "8px",
                  }}
                >
                  Email Address
                </TableCell>
                <TableCell
                  sx={{
                    textAlign: "center",
                    display: { xs: "none", md: "table-cell" },
                    fontWeight: "bold",
                    fontSize: { xs: "11px", sm: "14px", md: "15px" },
                    padding: "8px",
                  }}
                >
                  Unit Name
                </TableCell>
                <TableCell
                  sx={{
                    textAlign: "center",
                    display: { xs: "none", md: "table-cell" },
                    fontWeight: "bold",
                    fontSize: { xs: "11px", sm: "14px", md: "15px" },
                    padding: "8px",
                  }}
                >
                  Modem Serial Number
                </TableCell>
                <TableCell
                  sx={{
                    textAlign: "center",
                    display: { xs: "none", md: "table-cell" },
                    fontWeight: "bold",
                    fontSize: { xs: "11px", sm: "14px", md: "15px" },
                    padding: "8px",
                  }}
                >
                  Notification Group
                </TableCell>
                <TableCell
                  sx={{
                    display: { xs: "none", md: "table-cell" },
                    fontWeight: "bold",
                    fontSize: { xs: "11px", sm: "14px", md: "15px" },
                    textAlign: "center",
                    padding: "8px",
                  }}
                >
                  Delete
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedRecipients.length > 0 ? (
                paginatedRecipients.map((recipient, index) => (
                  <React.Fragment key={recipient.id || index}>
                    <TableRow
                      hover
                      sx={{
                        backgroundColor:
                          index % 2 === 0 ? "#2f3136" : "#3a3c43",
                        "&:hover": {
                          backgroundColor: "#4a4d54",
                        },
                      }}
                    >
                      {/* Expand Button - Mobile Only */}
                      <TableCell
                        sx={{
                          display: { xs: "table-cell", md: "none" },
                          textAlign: "center",
                        }}
                      >
                        <IconButton
                          size="small"
                          onClick={() =>
                            toggleRowExpansion(recipient.id || index)
                          }
                          sx={{ color: "white" }}
                        >
                          {expandedRows[recipient.id || index] ? (
                            <ExpandLessIcon />
                          ) : (
                            <ExpandMoreIcon />
                          )}
                        </IconButton>
                      </TableCell>

                      <TableCell
                        sx={{
                          textAlign: "center",
                          fontSize: { xs: "11px", sm: "14px", md: "15px" },
                        }}
                      >
                        {recipient.user?.name || ""}
                      </TableCell>

                      <TableCell
                        sx={{
                          textAlign: "center",
                          fontSize: { xs: "11px", sm: "14px", md: "15px" },
                        }}
                      >
                        {recipient.user?.email || ""}
                      </TableCell>

                      <TableCell
                        sx={{
                          textAlign: "center",
                          display: { xs: "none", md: "table-cell" },
                          fontSize: { xs: "11px", sm: "14px", md: "15px" },
                        }}
                      >
                        {recipient.device?.name || ""}
                      </TableCell>

                      <TableCell
                        sx={{
                          textAlign: "center",
                          display: { xs: "none", md: "table-cell" },
                          fontSize: { xs: "11px", sm: "14px", md: "15px" },
                        }}
                      >
                        {recipient.device?.id || ""}
                      </TableCell>

                      <TableCell
                        sx={{
                          textAlign: "center",
                          display: { xs: "none", md: "table-cell" },
                          fontSize: { xs: "11px", sm: "14px", md: "15px" },
                        }}
                      >
                        {recipient.groupName || "-"}
                      </TableCell>

                      <TableCell
                        sx={{
                          textAlign: "center",
                          display: { xs: "none", md: "table-cell" },
                          textAlign: "center",
                          fontSize: { xs: "11px", sm: "14px", md: "15px" },
                        }}
                      >
                        <Button
                          sx={{
                            textAlign: "center",
                            borderRadius: 0,
                          }}
                          variant="outlined"
                          color="error"
                          size="small"
                          onClick={() => handleDeleteClick(recipient)}
                        >
                          Delete
                        </Button>
                      </TableCell>
                    </TableRow>

                    {/* Mobile Collapsed Row */}
                    <TableRow sx={{ display: { xs: "table-row", md: "none" } }}>
                      <TableCell
                        style={{ paddingBottom: 0, paddingTop: 0 }}
                        colSpan={3}
                        sx={{
                          backgroundColor:
                            index % 2 === 0 ? "#2f3136" : "#3a3c43",
                        }}
                      >
                        <Collapse
                          in={expandedRows[recipient.id || index]}
                          timeout="auto"
                          unmountOnExit
                        >
                          <Box sx={{ py: 2, px: 1 }}>
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 1.5,
                              }}
                            >
                              <Box>
                                <Typography
                                  variant="caption"
                                  sx={{ color: "#999", fontWeight: "bold" }}
                                >
                                  Unit Name:
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{ color: "white" }}
                                >
                                  {recipient.device?.name || ""}
                                </Typography>
                              </Box>

                              <Box>
                                <Typography
                                  variant="caption"
                                  sx={{ color: "#999", fontWeight: "bold" }}
                                >
                                  Modem Serial Number:
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{ color: "white" }}
                                >
                                  {recipient.device?.id || ""}
                                </Typography>
                              </Box>

                              <Box>
                                <Typography
                                  variant="caption"
                                  sx={{ color: "#999", fontWeight: "bold" }}
                                >
                                  Notification Group:
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{ color: "white" }}
                                >
                                  {recipient.groupName || "-"}
                                </Typography>
                              </Box>

                              <Box sx={{ mt: 1 }}>
                                <Button
                                  sx={{
                                    borderRadius: 0,
                                  }}
                                  variant="outlined"
                                  color="error"
                                  size="small"
                                  fullWidth
                                  onClick={() => handleDeleteClick(recipient)}
                                >
                                  Delete
                                </Button>
                              </Box>
                            </Box>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    {loadingData ? "Loading..." : "No recipients found"}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={filteredRecipients.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50, 100]}
          sx={{ mt: 2 }}
        />

        <Menu
          anchorEl={exportMenuAnchor}
          open={Boolean(exportMenuAnchor)}
          onClose={handleExportMenuClose}
        >
          <MenuItem onClick={copyToClipboard}>
            <ListItemIcon>
              <FileCopyIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Copy</ListItemText>
          </MenuItem>
          <MenuItem onClick={exportToCSV}>
            <ListItemIcon>
              <DescriptionIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>CSV</ListItemText>
          </MenuItem>
          <MenuItem onClick={exportToExcel}>
            <ListItemIcon>
              <TableViewIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Excel</ListItemText>
          </MenuItem>
          <MenuItem onClick={exportToPDF}>
            <ListItemIcon>
              <PictureAsPdfIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>PDF</ListItemText>
          </MenuItem>
        </Menu>

        <ConfirmDialog
          open={confirmOpen}
          onClose={() => setConfirmOpen(false)}
          onConfirm={() => handleDeleteRecipient(selectedRecipient)}
          title="Confirm Delete"
          body={
            selectedRecipient?.source === "membership"
              ? `This user is linked to this device through the notification group "${selectedRecipient?.groupName}". Deleting this membership means they will no longer receive any notifications from all devices linked to this group.\nAre you sure you want to proceed?`
              : `Are you sure you want to delete this recipient from this device?`
          }
          confirmText="Delete"
        />
      </Paper>
    </Box>
  );
}
